import { Card, Grid, Group, Loader, SegmentedControl, Stack, Text, Title } from '@mantine/core';
import { BarChart, LineChart } from '@mantine/charts';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { unwrap } from '../api/client';
import { inboxAnalytics } from '../api/gen';
import { useOrg } from '../api/org';
import { duration, percent } from '../lib/format';

// Whether the inbox is working, in the four numbers an operator checks and two
// series they scan for a shape. Nothing here is a target — the page shows what
// happened, and the median first response is the one figure worth watching.
export function AnalyticsPage() {
  const org = useOrg();
  const [days, setDays] = useState('30');

  const { data, isLoading } = useQuery({
    queryKey: ['analytics', days],
    queryFn: () => unwrap(inboxAnalytics({ headers: org, query: { days: Number(days) } })),
  });

  if (isLoading || !data) return <Loader size="sm" />;

  const byDay = new Map<string, Record<string, number | string>>();
  for (const row of data.conversationsPerDay) {
    const day = row.day.slice(5);
    const entry = byDay.get(day) ?? { day };
    entry[row.channel] = ((entry[row.channel] as number) ?? 0) + row.total;
    byDay.set(day, entry);
  }
  const channels = [...new Set(data.conversationsPerDay.map((r) => r.channel))];
  const palette = ['blue.6', 'grape.6', 'teal.6', 'orange.6'];

  const messages = new Map<string, Record<string, number | string>>();
  for (const row of data.messagesPerDay) {
    const day = row.day.slice(5);
    const entry = messages.get(day) ?? { day };
    entry[row.direction] = ((entry[row.direction] as number) ?? 0) + row.total;
    messages.set(day, entry);
  }

  const answered = data.answeredTotal > 0 ? data.answeredByAgent / data.answeredTotal : 0;

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={3}>How it is going</Title>
        <SegmentedControl
          size="xs"
          value={days}
          onChange={setDays}
          data={[
            { value: '7', label: '7 days' },
            { value: '30', label: '30 days' },
            { value: '90', label: '90 days' },
          ]}
        />
      </Group>

      <Grid>
        <Figure label="Conversations" value={String(data.opened)} />
        <Figure label="Closed" value={String(data.resolved)} hint="Of those opened, closed now" />
        <Figure
          label="First answer"
          value={duration(data.medianFirstResponseSeconds)}
          hint="Median, not mean"
        />
        <Figure
          label="Answered by the agent"
          value={percent(answered)}
          hint={`${data.answeredByAgent} of ${data.answeredTotal}`}
        />
      </Grid>

      <Card withBorder padding="md">
        <Text size="sm" fw={600} mb="sm">
          Where the work came from
        </Text>
        {byDay.size === 0 ? (
          <Text size="sm" c="dimmed">
            Nothing in this window.
          </Text>
        ) : (
          <BarChart
            h={220}
            data={[...byDay.values()]}
            dataKey="day"
            type="stacked"
            series={channels.map((c, i) => ({ name: c, color: palette[i % palette.length]! }))}
          />
        )}
      </Card>

      <Card withBorder padding="md">
        <Text size="sm" fw={600} mb="sm">
          Messages in and out
        </Text>
        {messages.size === 0 ? (
          <Text size="sm" c="dimmed">
            Nothing in this window.
          </Text>
        ) : (
          <LineChart
            h={220}
            data={[...messages.values()]}
            dataKey="day"
            series={[
              { name: 'inbound', color: 'blue.6' },
              { name: 'outbound', color: 'teal.6' },
            ]}
          />
        )}
      </Card>
    </Stack>
  );
}

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Grid.Col span={{ base: 6, md: 3 }}>
      <Card withBorder padding="md">
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        <Text fw={700} size="xl">
          {value}
        </Text>
        {hint ? (
          <Text size="xs" c="dimmed">
            {hint}
          </Text>
        ) : null}
      </Card>
    </Grid.Col>
  );
}
